import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Minus, RefreshCw, ArrowRight } from 'lucide-react'
import { useCrawlHistory, useCrawlComparison } from '../hooks/useCrawls'

/** Human-readable field labels */
const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  meta_description: 'Meta Description',
  status_code: 'Status Code',
  canonical_status: 'Canonical',
  noindex: 'Noindex',
  has_schema: 'Schema',
  has_og_tags: 'OG Tags',
  word_count: 'Word Count',
  response_time_ms: 'Response Time',
}

function formatDate(iso: string | null) {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (field === 'response_time_ms') return `${value}ms`
  if (field === 'word_count') return `${value} words`
  return String(value)
}

/** Summary stat card */
function StatCard({ label, count, icon, color }: { label: string; count: number; icon: React.ReactNode; color: string }) {
  return (
    <div className={`rounded-lg border p-3 text-center ${color}`}>
      <div className="mx-auto mb-1">{icon}</div>
      <p className="text-2xl font-bold">{count}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}

/** Comparison picker — select a crawl from domain history */
function CrawlPicker({
  crawlId,
  onSelect,
  selectedId,
}: {
  crawlId: string
  onSelect: (id: string) => void
  selectedId?: string | null
}) {
  const { data, isLoading } = useCrawlHistory(crawlId, true)

  const crawls = data?.crawls || []
  const completedCrawls = crawls.filter((c) => c.status === 'completed')

  if (isLoading) return <Skeleton className="h-10 w-full max-w-sm" />

  if (completedCrawls.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No other crawls found for this domain. Run another crawl to enable comparison.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Select a previous crawl to compare against:
      </p>
      <Select value={selectedId ?? undefined} onValueChange={onSelect}>
        <SelectTrigger className="max-w-md">
          <SelectValue placeholder="Choose a crawl..." />
        </SelectTrigger>
        <SelectContent>
          {completedCrawls.map((crawl) => (
            <SelectItem key={crawl.id} value={crawl.id}>
              {formatDate(crawl.completed_at)} — {crawl.pages_crawled} pages · {crawl.issues_count} issue{crawl.issues_count !== 1 ? 's' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

/** Change field badge showing before → after */
function ChangeBadge({ field, before, after }: { field: string; before: unknown; after: unknown }) {
  const label = FIELD_LABELS[field] || field

  // Determine if change is positive/negative/neutral
  let color = 'text-yellow-600 dark:text-yellow-400'
  if (field === 'status_code') {
    const b = Number(before), a = Number(after)
    if (a >= 400 && b < 400) color = 'text-destructive'
    else if (a < 400 && b >= 400) color = 'text-green-600 dark:text-green-400'
  } else if (field === 'noindex') {
    color = after ? 'text-destructive' : 'text-green-600 dark:text-green-400'
  }

  return (
    <span className={`inline-flex items-center gap-1 text-xs ${color}`}>
      <span className="font-medium">{label}:</span>
      <span className="line-through opacity-60">{formatValue(field, before)}</span>
      <ArrowRight className="h-3 w-3" />
      <span>{formatValue(field, after)}</span>
    </span>
  )
}

/** Summary dashboard */
function CompareSummary({ data }: { data: NonNullable<ReturnType<typeof useCrawlComparison>['data']> }) {
  const { summary } = data

  // Field change breakdown bar
  const fieldEntries = Object.entries(summary.byField).sort(([, a], [, b]) => b - a)

  return (
    <div className="space-y-4">
      {/* Top-level stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          label="Pages Added"
          count={summary.added}
          icon={<Plus className="mx-auto h-5 w-5 text-green-600 dark:text-green-400" />}
          color={summary.added > 0 ? 'border-green-500/30 bg-green-500/5' : ''}
        />
        <StatCard
          label="Pages Removed"
          count={summary.removed}
          icon={<Minus className="mx-auto h-5 w-5 text-destructive" />}
          color={summary.removed > 0 ? 'border-destructive/30 bg-destructive/5' : ''}
        />
        <StatCard
          label="Pages Changed"
          count={summary.changed}
          icon={<RefreshCw className="mx-auto h-5 w-5 text-yellow-500" />}
          color={summary.changed > 0 ? 'border-yellow-500/30 bg-yellow-500/5' : ''}
        />
      </div>

      {/* Crawl comparison header */}
      <div className="flex items-center gap-3 text-sm">
        <div className="rounded-lg border px-3 py-2 flex-1">
          <p className="text-muted-foreground text-xs">Previous</p>
          <p className="font-medium">{formatDate(data.crawl1.completedAt)}</p>
          <p className="text-xs text-muted-foreground">{data.crawl1.pagesCrawled} pages · {data.crawl1.issuesCount} issues</p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="rounded-lg border px-3 py-2 flex-1">
          <p className="text-muted-foreground text-xs">Current</p>
          <p className="font-medium">{formatDate(data.crawl2.completedAt)}</p>
          <p className="text-xs text-muted-foreground">{data.crawl2.pagesCrawled} pages · {data.crawl2.issuesCount} issues</p>
        </div>
      </div>

      {/* Change breakdown by field */}
      {fieldEntries.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Changes by Field</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {fieldEntries.map(([field, count]) => {
                const maxCount = fieldEntries[0]?.[1] ?? 1
                const pct = Math.round((count / maxCount) * 100)
                return (
                  <div key={field} className="flex items-center gap-3 text-sm">
                    <span className="w-32 text-xs text-muted-foreground truncate">{FIELD_LABELS[field] || field}</span>
                    <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-500/60 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium w-8 text-right">{count}</span>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export function CompareTab({ crawlId, initialOtherId }: { crawlId: string; initialOtherId?: string }) {
  const [compareId, setCompareId] = useState<string | null>(initialOtherId ?? null)
  const { data, isLoading } = useCrawlComparison(crawlId, compareId)

  // Auto-select when initialOtherId changes (e.g. navigated from History tab)
  useEffect(() => {
    if (initialOtherId) setCompareId(initialOtherId)
  }, [initialOtherId])

  return (
    <div className="space-y-6">
      <CrawlPicker crawlId={crawlId} onSelect={setCompareId} selectedId={compareId} />

      {isLoading && compareId && <Skeleton className="h-48 w-full" />}

      {data && (
        <>
          <CompareSummary data={data} />

          {/* Detailed changes */}
          {data.pages.added.length === 0 && data.pages.removed.length === 0 && data.pages.changed.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No differences found between these crawls.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Added pages */}
              {data.pages.added.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Plus className="h-4 w-4 text-green-600 dark:text-green-400" />
                      New Pages ({data.pages.added.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableBody>
                          {data.pages.added.map((url) => (
                            <TableRow key={url}>
                              <TableCell className="font-mono text-xs truncate max-w-[500px]" title={url}>
                                {url}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Removed pages */}
              {data.pages.removed.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Minus className="h-4 w-4 text-destructive" />
                      Removed Pages ({data.pages.removed.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableBody>
                          {data.pages.removed.map((url) => (
                            <TableRow key={url}>
                              <TableCell className="font-mono text-xs truncate max-w-[500px]" title={url}>
                                {url}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Changed pages */}
              {data.pages.changed.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 text-yellow-500" />
                      Changed Pages ({data.pages.changed.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[500px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="min-w-[250px]">URL</TableHead>
                            <TableHead>Changes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.pages.changed.map(({ url, changes }) => (
                            <TableRow key={url}>
                              <TableCell className="font-mono text-xs truncate max-w-[250px] align-top" title={url}>
                                {url}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                  {changes.map((ch) => (
                                    <ChangeBadge key={ch.field} field={ch.field} before={ch.before} after={ch.after} />
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
